// PlaidLink.js - Manual implementation without react-plaid-link package
import React, { useEffect, useState, useCallback } from 'react';
import { plaidAPI } from '../services/api';

const PlaidLinkComponent = ({ onSuccess, onError }) => {
  const [loading, setLoading] = useState(true);
  const [plaidLinkInstance, setPlaidLinkInstance] = useState(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  const initializePlaid = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Initializing Plaid...');

      const { link_token } = await plaidAPI.createLinkToken();
      console.log('Got link token:', link_token);

      if (!window.Plaid) {
        throw new Error('Plaid script not loaded');
      }

      // Initialize Plaid Link
      const linkHandler = window.Plaid.create({
        token: link_token,
        onSuccess: async (public_token, metadata) => {
          console.log('Plaid Link success:', metadata);
          try {
            setLoading(true);
            const result = await plaidAPI.exchangePublicToken(public_token, metadata);
            console.log('Token exchange result:', result);
            onSuccess?.(result);
          } catch (error) {
            console.error('Error exchanging token:', error);
            onError?.(error);
          } finally {
            setLoading(false);
          }
        },
        onExit: (err, metadata) => {
          console.log('Plaid Link exit:', err, metadata);
          if (err) {
            console.error('Plaid Link exited with error:', err);
            onError?.(err);
          }
          setLoading(false);
        },
        onEvent: (eventName, metadata) => {
          console.log('Plaid event:', eventName, metadata);
        },
      });

      setPlaidLinkInstance(linkHandler);
      console.log('Plaid Link initialized successfully');
    } catch (error) {
      console.error('Error initializing Plaid:', error);
      onError?.(error);
    } finally {
      setLoading(false);
    }
  }, [onSuccess, onError]);

  useEffect(() => {
    // Check if script is already loaded
    if (window.Plaid) {
      setScriptLoaded(true);
      initializePlaid();
      return;
    }

    // Load Plaid Link script if not already present
    let script = document.querySelector('script[src*="plaid.com"]');
    if (!script) {
      script = document.createElement('script');
      script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
      script.async = true;
      script.onload = () => {
        setScriptLoaded(true);
        initializePlaid();
      };
      script.onerror = () => {
        console.error('Failed to load Plaid script');
        setLoading(false);
        onError?.({ message: 'Failed to load Plaid script' });
      };
      document.head.appendChild(script);
    }
  }, [initializePlaid, onError]);

  const handleClick = () => {
    console.log('Connect button clicked');
    if (plaidLinkInstance) {
      console.log('Opening Plaid Link...');
      plaidLinkInstance.open();
    } else {
      console.error('Plaid Link instance not ready');
    }
  };

  if (loading && !scriptLoaded) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2 text-gray-300">Loading Plaid...</span>
      </div>
    );
  }

  if (loading && scriptLoaded) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2 text-gray-300">Initializing...</span>
      </div>
    );
  }

  return (
    <button
      onClick={handleClick}
      disabled={!plaidLinkInstance || loading}
      className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition-colors"
    >
      {loading ? 'Processing...' : 'Connect Bank Account'}
    </button>
  );
};

export default PlaidLinkComponent;
